/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  clearMessages,
  clearMessagesAndAddWelcome,
  selectAllUserSelectionsFromAllMessages,
  setDocumentToSearchFor,
} from './chatSlice';
import { AudienceType, HowActivatedType } from '../algorithm/types';
// import { useDependenciesDebugger } from '../useDebugHooks';

export function useAudienceFromSearchParamsOrLocalStorage(
  redirectIfNotFound_: boolean,
  disableAudience: boolean
): [AudienceType, (audience: AudienceType) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  // For these, undefined means not set (so use the default) whereas null means 'any'
  const persona_ =
    !disableAudience && searchParams.has('persona') ? convertNull(searchParams.get('persona')) : undefined;
  const careCategory_ =
    !disableAudience && searchParams.has('careCategory') ? convertNull(searchParams.get('careCategory')) : undefined;
  const patientGroup_ =
    !disableAudience && searchParams.has('patientGroup') ? convertNull(searchParams.get('patientGroup')) : undefined;
  const documentVersionId = searchParams.get(SEARCH_PARAM.DOCUMENT_VERSION);
  const documentId = searchParams.get(SEARCH_PARAM.DOCUMENT);

  const redirectIfNotFound = redirectIfNotFound_ && !documentVersionId && !documentId;

  const defaultAudience = useMemo(() => {
    if (disableAudience) return { persona: '', careCategory: '', patientGroup: '' };

    let audience = JSON.parse(localStorage.getItem('chat.audience') || '{}') as AudienceType;
    return {
      persona: audience.persona === undefined ? 'Provider' : convertNull(audience.persona),
      careCategory: audience.careCategory === undefined ? 'Outpatient' : convertNull(audience.careCategory),
      patientGroup: convertNull(audience.patientGroup), // Default to both Adult and Pediatric
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Note that defaultAudience.* can be empty string and means no filter, and that is valid.
  // Don't set to the default audience if the search param is empty string.
  // Note also that the defaultAudience is what it is local storage.
  const persona = (persona_ === undefined ? defaultAudience.persona : persona_) || '';
  const careCategory = (careCategory_ === undefined ? defaultAudience.careCategory : careCategory_) || '';
  const patientGroup = (patientGroup_ === undefined ? defaultAudience.patientGroup : patientGroup_) || '';

  const audience = useMemo(() => {
    return { persona, careCategory, patientGroup };
  }, [persona, careCategory, patientGroup]);

  useEffect(() => {
    if (redirectIfNotFound) {
      if (!disableAudience) {
        searchParams.set('persona', audience.persona);
        searchParams.set('careCategory', audience.careCategory);
        searchParams.set('patientGroup', audience.patientGroup);
      } else {
        searchParams.delete('persona');
        searchParams.delete('careCategory');
        searchParams.delete('patientGroup');
      }
      // Note that setSearchParams calls the router navigate(), which must be called in a
      // useEffect or setTimeout or similar
      if (window.location.search !== '?' + searchParams.toString()) {
        console.log(
          'useAudienceFromSearchParamsOrLocalStorage calling setSearchParams, which will cause the current page to reload from',
          window.location.search,
          'to',
          searchParams.toString()
        );
        // console.trace('Stack dump:');
        setSearchParams(searchParams, { replace: true }); // replace means don't add to history
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience, redirectIfNotFound]);

  const setAudience = (audience_: AudienceType) => {
    const audience = { persona, careCategory, patientGroup, ...audience_ };
    localStorage.setItem('chat.audience', JSON.stringify(audience));
    const newSearchParams = new URLSearchParams(searchParams.toString());
    if (!disableAudience) {
      newSearchParams.set('persona', audience.persona || '');
      newSearchParams.set('careCategory', audience.careCategory || '');
      newSearchParams.set('patientGroup', audience.patientGroup || '');
    } else {
      newSearchParams.delete('persona');
      newSearchParams.delete('careCategory');
      newSearchParams.delete('patientGroup');
    }
    setSearchParams(newSearchParams, { replace: true });
  };
  return [audience, setAudience];
}

export const SEARCH_PARAM = {
  DOCUMENT_VERSION: 'documentversion',
  DOCUMENT: 'document',
  TITLE: 'title',
  PAGE: 'page',
  SLUG: 'slug',
} as const;

type ChatNavigationState = {
  skipTopicLoad?: boolean;
  guideTitle?: string;
  chatSearch?: {
    text: string;
    slug?: string;
    keep?: boolean;
    howActivated: HowActivatedType;
  };
};

function topicSearchParams(slugOrTitleKey: 'slug' | 'title', value: string, publishedOnly: boolean) {
  const searchParams = new URLSearchParams();
  searchParams.set(slugOrTitleKey, value);
  searchParams.set('onlyPublished', publishedOnly ? 'true' : 'false');
  return searchParams;
}

export function useNavigateToTopic(publishedOnly: boolean, pathname?: string) {
  const location = useLocation();
  const navigate = useNavigate();

  return useCallback(
    (text: string, slug: string | undefined, howActivated: HowActivatedType, keep = false) => {
      const searchParams = topicSearchParams(
        slug ? SEARCH_PARAM.SLUG : SEARCH_PARAM.TITLE,
        slug || text,
        publishedOnly
      );

      navigate(
        { pathname: pathname || location.pathname, search: searchParams.toString() },
        { state: { guideTitle: text, chatSearch: { text, slug, keep, howActivated } } }
      );
    },
    [location.pathname, navigate, pathname, publishedOnly]
  );
}

export function useReplaceCurrentTopic(publishedOnly: boolean) {
  const location = useLocation();
  const navigate = useNavigate();

  return useCallback(
    (slug: string, guideTitle: string) => {
      navigate(
        {
          pathname: location.pathname,
          search: topicSearchParams(SEARCH_PARAM.SLUG, slug, publishedOnly).toString(),
        },
        { replace: true, state: { skipTopicLoad: true, guideTitle } }
      );
    },
    [location.pathname, navigate, publishedOnly]
  );
}

/**
 *  This will call: openDocumentByIdThunk if a document id is passed in the url search param,
 *  and call textInputThunk if text is passed in the url.
 *
 *  Here is the logic to decide what document to show and when to update:
 *
 *    1. User goes to #foo (text)
 *        This always takes priority. Run this as textInput.
 *        Once the document is loaded, remove the text from the url and replace with the slug.
 *        DEPS: text, audience, publishedOnly
 *
 *    2. User goes to:  chat?documentversion=abc_id
 *        Load this document version, and don't change the url.
 *        NO DEPS.
 *
 *    3. User goes to:  chat?document=abc_id
 *       Load this document (latest version), and don't change the url.
 *       DEPS: publishedOnly
 *
 *    4. User goes to:  chat?slug=foo
 *       Load this document, and don't change the url.
 *       DEPS: publishedOnly
 */
export function useSearchParamsDocument(audience: AudienceType, publishedOnly: boolean, searchCategories: boolean) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigationSearch =
    navigationType === 'PUSH' ? (location.state as ChatNavigationState | null)?.chatSearch : undefined;
  const skipTopicLoad = navigationType === 'REPLACE' && (location.state as ChatNavigationState | null)?.skipTopicLoad;
  const paramsDocumentId = searchParams.get(SEARCH_PARAM.DOCUMENT);
  const paramsDocumentTitle = searchParams.get(SEARCH_PARAM.TITLE);
  const paramsDocumentVersionId = searchParams.get(SEARCH_PARAM.DOCUMENT_VERSION);
  const paramsSlug = searchParams.get(SEARCH_PARAM.SLUG);
  const paramsPage = searchParams.get(SEARCH_PARAM.PAGE);
  const dispatch = useAppDispatch();
  let hashText = '';
  try {
    hashText = decodeURIComponent(location.hash.slice(1));
  } catch (e) {
    console.error('Failed to decode hash', location.hash, e);
  }

  /*useDependenciesDebugger({
    careCategory: audience.careCategory,
    persona: audience.persona,
    patientGroup: audience.patientGroup,
    dispatch,
    hashText,
    paramsDocumentId,
    paramsDocumentTitle,
    paramsDocumentVersionId,
    paramsPage,
    paramsSlug,
    publishedOnly,
    searchCategories,
    searchParams,
    searchParams_str: searchParams.toString(),
    setSearchParams,
  });*/
  useEffect(() => {
    if (skipTopicLoad) return;

    // We have no document open.  Check search params if we should automatically load one
    if (!hashText && !paramsDocumentId && !paramsDocumentVersionId && !paramsDocumentTitle && !paramsSlug) {
      // We have no document open, and no search param in the url like ?document= or ?documentversion=
      // So nothing to do
      return;
    }

    // Set audience again, in case it has changed
    // clone the searchParams so we can modify it
    const newSearchParams = new URLSearchParams(searchParams.toString());
    //newSearchParams.set('persona', audience.persona || '');
    //newSearchParams.set('careCategory', audience.careCategory || '');
    //newSearchParams.set('patientGroup', audience.patientGroup || '');
    if (hashText) {
      newSearchParams.set(SEARCH_PARAM.TITLE, hashText);
      // This overrides everything else, so remove it from the url
      newSearchParams.delete(SEARCH_PARAM.DOCUMENT);
      newSearchParams.delete(SEARCH_PARAM.DOCUMENT_VERSION);
      newSearchParams.delete(SEARCH_PARAM.SLUG);
    } else if (paramsDocumentVersionId) {
      // This is a document version, so remove the other search params
      newSearchParams.delete(SEARCH_PARAM.DOCUMENT);
      newSearchParams.delete(SEARCH_PARAM.SLUG);
    } else if (paramsDocumentId) {
      // This is a document, so remove the other search params
      newSearchParams.delete(SEARCH_PARAM.SLUG);
    }

    if (searchParams.toString() !== newSearchParams.toString()) {
      console.log(
        'Search parameters changed, updating search params to',
        newSearchParams.toString(),
        'from',
        searchParams.toString(),
        'hashText is',
        hashText
      );
      // Note, under the hood this is simply calling navigate()
      setSearchParams(newSearchParams, { replace: true });
      return; // We will be called again with the new search params
    }
    setBrowserTitle(`${hashText || paramsDocumentTitle || paramsSlug || ''}`);
    // Note, we don't clear the hash here. If we have a hash, then we would search for that
    // document, then when that document is loaded, then the hash is removed and replaced with the slug.

    /*
      The below setDocumentToSeachFor will trigger the following:

      openDocumentByIdThunk({
        documentId: paramsDocumentId,
        documentTitle: paramsDocumentTitle,
        documentVersionId: paramsDocumentVersionId,
        slug: paramsSlug,
        audience,
        publishedOnly,
      })

      The reason for the indirection is because otherwise you get the same document opened mulitple times as various useEffects are triggered from the audience
      changing.
    */

    if (hashText === '_') {
      // In the special case of it being #_, then clear the current messages
      // This will also clear the hash
      dispatch(clearMessagesAndAddWelcome());
    } else {
      // We have no document open, but we want to automatically open one
      if (!navigationSearch?.keep) dispatch(clearMessages());
      // It's a two step - openDocumentByIdThunk will be called next
      dispatch(
        setDocumentToSearchFor({
          audience: {
            persona: audience.persona,
            careCategory: audience.careCategory,
            patientGroup: audience.patientGroup,
          },
          documentId: paramsDocumentId || undefined,
          documentVersionId: paramsDocumentVersionId || undefined,
          text:
            navigationSearch?.text ||
            hashText ||
            (!paramsDocumentId && !paramsDocumentVersionId && !paramsSlug
              ? paramsDocumentTitle || undefined
              : undefined),
          page: paramsPage || undefined,
          slug: navigationSearch?.slug || paramsSlug || undefined,
          publishedOnly,
          searchCategories,
          keep: navigationSearch?.keep || false,
          howActivated: navigationSearch?.howActivated || 'urlLink',
        })
      );
    }
  }, [
    audience.careCategory,
    audience.patientGroup,
    audience.persona,
    dispatch,
    hashText,
    paramsDocumentId,
    paramsDocumentTitle,
    paramsDocumentVersionId,
    paramsPage,
    paramsSlug,
    publishedOnly,
    searchCategories,
    searchParams,
    setSearchParams,
    skipTopicLoad,
    navigationSearch?.howActivated,
    navigationSearch?.keep,
    navigationSearch?.slug,
    navigationSearch?.text,
  ]);
}

let browserTitleBase = 'MedPearl';
export function setBrowserTitleBase(base: string) {
  browserTitleBase = base;
}

export function setBrowserTitle(title: string) {
  document.title = title ? `${browserTitleBase} - ${title}` : browserTitleBase;
}

/* Update the browser title */
export function useTitle(title: string) {
  useEffect(() => {
    setBrowserTitle(title || '');
    return () => {
      setBrowserTitle('');
    };
  }, [title]);
}

export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>();
  useEffect(() => {
    ref.current = value; //assign the value of ref to the argument
  }, [value]); //this code will run when the value of 'value' changes
  return ref.current; //in the end, return the current ref value.
}

// URLSearchParms will use a literal "null" to indicate a null value
// We treat null to mean 'no filter'
// But keep undefined as undefined, to mean the default
function convertNull(str: string | null | undefined) {
  if (str === undefined) return undefined;
  if (!str || str === 'null' || str === '_null_' || str === 'undefined') {
    return null;
  }
  return str;
}

/**
 * Add a shadow to the bottom of the scrollable element
 * Use like:
 *   const { boxShadow, onScrollHandler } = useScrollWithShadow();
 *   return (
 *     <div onScroll={onScrollHandler} style={{ boxShadow }}>
 *       ... Scroll area here ...
 *     </div>
 *   );
 */
export function useScrollWithShadow() {
  const [scrollTop, setScrollTop] = useState<undefined | number>(0);
  const [scrollHeight, setScrollHeight] = useState<undefined | number>(0);
  const [clientHeight, setClientHeight] = useState<undefined | number>(0);

  const onScrollHandler = useCallback((event: { target: any | null }) => {
    setScrollTop(event?.target?.scrollTop);
    setScrollHeight(event?.target?.scrollHeight);
    setClientHeight(event?.target?.clientHeight);
  }, []);

  const boxShadow = useMemo(() => {
    if (scrollHeight === undefined || clientHeight === undefined || scrollTop === undefined) {
      return { boxShadow: 'none', scroll: 'none' };
    }
    const isBottom = clientHeight >= scrollHeight - scrollTop - 1; // -1 for rounding
    const isTop = scrollTop === 0;
    let boxShadow = 'none';
    const top = 'inset 0 8px 5px -5px rgb(200 200 200 / 1)';
    const bottom = 'inset 0 -8px 5px -5px rgb(200 200 200 / 1)';
    let scroll = 'none';

    if (isTop && !isBottom) {
      boxShadow = bottom;
      scroll = 'bottom';
    } else if (!isTop && !isBottom) {
      boxShadow = `${top}, ${bottom}`;
      scroll = 'both';
    } else if (isBottom && !isTop) {
      boxShadow = top;
      scroll = 'top';
    }
    return { boxShadow, scroll };
  }, [clientHeight, scrollHeight, scrollTop]);

  return { boxShadow: boxShadow.boxShadow, scroll: boxShadow.scroll, onScrollHandler };
}

export function useScrollDirection(ref: React.RefObject<HTMLDivElement>) {
  const [scrollDirection, setScrollDirection] = useState<'down' | 'up' | null>(null);
  const div = ref.current;

  useEffect(() => {
    let lastScrollY = div?.scrollTop;

    const updateScrollDirection = () => {
      const scrollY = div?.scrollTop;
      if (lastScrollY === undefined || scrollY === undefined) {
        lastScrollY = scrollY;
        setScrollDirection(null);
        return;
      }
      const direction = scrollY > lastScrollY ? 'down' : 'up';
      if (scrollY - lastScrollY > 10 || scrollY - lastScrollY < -10) {
        if (direction !== scrollDirection) {
          setScrollDirection(direction);
        }
        lastScrollY = scrollY > 0 ? scrollY : 0;
      }
    };
    div?.addEventListener('scroll', updateScrollDirection); // add event listener
    return () => {
      div?.removeEventListener('scroll', updateScrollDirection); // clean up
    };
  }, [div, scrollDirection]);

  return scrollDirection;
}

/**
 * Custom hook to get all user selections till the given message index
 * @param messageIndex return the selections till the message index
 * @param showOnlyMessageIndex return selections only matching with given message index, if passed
 */
export function useAllUserSelectionsFromAllMessages(messageIndex: number, showOnlyMessageIndex?: number) {
  const selectedAlgoKeys = useAppSelector((state) => selectAllUserSelectionsFromAllMessages(state));
  const filtered = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(selectedAlgoKeys).filter(([, selectedButtonInfo]) =>
          !!showOnlyMessageIndex
            ? selectedButtonInfo.messageIndex === showOnlyMessageIndex
            : selectedButtonInfo.messageIndex <= messageIndex
        )
      ),
    [messageIndex, selectedAlgoKeys, showOnlyMessageIndex]
  );
  return filtered;
}
